import React, { useState, useEffect, useRef, useImperativeHandle, forwardRef } from 'react';
import { X, Ruler, Square } from 'lucide-react';
import Map from 'ol/Map';
import View from 'ol/View';
import TileLayer from 'ol/layer/Tile';
import VectorTileLayer from 'ol/layer/VectorTile';
import VectorLayer from 'ol/layer/Vector';
import OSM from 'ol/source/OSM';
import XYZ from 'ol/source/XYZ';
import VectorTileSource from 'ol/source/VectorTile';
import VectorSource from 'ol/source/Vector';
import MVT from 'ol/format/MVT';
import Overlay from 'ol/Overlay';
import Feature from 'ol/Feature';
import Point from 'ol/geom/Point';
import { fromLonLat, toLonLat } from 'ol/proj';
import { Fill, Stroke, Style, Circle as CircleStyle, Text } from 'ol/style';
import Draw from 'ol/interaction/Draw';
import { getLength, getArea } from 'ol/sphere';
import { unByKey } from 'ol/Observable';
import 'ol/ol.css';
import { getLayerSymbology } from '../utils/layerStyleMapper';
import {
  getActiveLayerIds,
  createMultiLayerUrlTemplate,
  fetchMultiLayerMetadata,
} from '../utils/multiLayerTileLoader';

const MapComponent = forwardRef(({ layers, layerMetadata, onMapClick, searchLocation }, ref) => {
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const layerMetadataRef = useRef({});
  const layersRef = useRef({}); // Store OpenLayers layer references
  const popupRef = useRef(null);
  const overlayRef = useRef(null);
  const searchMarkerRef = useRef(null);
  const currentLocationMarkerRef = useRef(null);
  const measureLayerRef = useRef(null);
  const drawInteractionRef = useRef(null);
  const measureTooltipElementRef = useRef(null);
  const measureTooltipRef = useRef(null);
  const helpTooltipElementRef = useRef(null);
  const helpTooltipRef = useRef(null);
  const sketchRef = useRef(null);
  const listenerRef = useRef(null);
  const measurementTypeRef = useRef(null);
  const cogLayerRef = useRef(null);
  const osmLayerRef = useRef(null);
  const multiLayerRef = useRef(null); // Reference to combined multi-layer
  const [featureInfo, setFeatureInfo] = useState(null);
  const [measurementType, setMeasurementType] = useState(null); // 'length' or 'area'

  // Measurement helper functions
  const formatLength = (line) => {
    const length = getLength(line);
    let output;
    if (length > 100) {
      output = Math.round((length / 1000) * 100) / 100 + ' km';
    } else {
      output = Math.round(length * 100) / 100 + ' m';
    }
    return output;
  };

  const formatArea = (polygon) => {
    const area = getArea(polygon);
    let output;
    if (area > 10000) {
      output = Math.round((area / 1000000) * 100) / 100 + ' km²';
    } else {
      output = Math.round(area * 100) / 100 + ' m²';
    }
    return output;
  };

  const createMeasureTooltip = () => {
    if (measureTooltipElementRef.current && measureTooltipElementRef.current.parentNode) {
      measureTooltipElementRef.current.parentNode.removeChild(measureTooltipElementRef.current);
    }
    measureTooltipElementRef.current = document.createElement('div');
    measureTooltipElementRef.current.className = 'ol-tooltip ol-tooltip-measure';
    // Apply inline styles directly
    Object.assign(measureTooltipElementRef.current.style, {
      position: 'absolute',
      background: 'rgba(0, 0, 0, 0.7)',
      borderRadius: '4px',
      color: 'white',
      padding: '4px 8px',
      opacity: '1',
      whiteSpace: 'nowrap',
      fontSize: '12px',
      fontWeight: 'bold',
      pointerEvents: 'none'
    });
    measureTooltipRef.current = new Overlay({
      element: measureTooltipElementRef.current,
      offset: [0, -15],
      positioning: 'bottom-center',
      stopEvent: false,
    });
    mapRef.current.addOverlay(measureTooltipRef.current);
  };

  const createHelpTooltip = () => {
    if (helpTooltipElementRef.current && helpTooltipElementRef.current.parentNode) {
      helpTooltipElementRef.current.parentNode.removeChild(helpTooltipElementRef.current);
    }
    helpTooltipElementRef.current = document.createElement('div');
    helpTooltipElementRef.current.className = 'ol-tooltip hidden';
    Object.assign(helpTooltipElementRef.current.style, {
      position: 'absolute',
      background: 'rgba(0, 0, 0, 0.7)',
      borderRadius: '4px',
      color: 'white',
      padding: '4px 8px',
      whiteSpace: 'nowrap',
      fontSize: '12px',
      pointerEvents: 'none'
    });
    helpTooltipRef.current = new Overlay({
      element: helpTooltipElementRef.current,
      offset: [15, 0],
      positioning: 'center-left',
    });
    mapRef.current.addOverlay(helpTooltipRef.current);
  };

  const addMeasureInteraction = (type) => {
    if (!mapRef.current) return;

    const map = mapRef.current;

    // Create measurement layer if it doesn't exist
    if (!measureLayerRef.current) {
      const source = new VectorSource();
      measureLayerRef.current = new VectorLayer({
        source: source,
        style: new Style({
          fill: new Fill({
            color: 'rgba(239, 68, 68, 0.2)',
          }),
          stroke: new Stroke({
            color: '#ef4444',
            width: 4,
          }),
          image: new CircleStyle({
            radius: 8,
            fill: new Fill({
              color: '#ef4444',
            }),
            stroke: new Stroke({
              color: '#ffffff',
              width: 3,
            }),
          }),
        }),
      });
      map.addLayer(measureLayerRef.current);
    }

    const drawType = type === 'area' ? 'Polygon' : 'LineString';
    const source = measureLayerRef.current.getSource();

    drawInteractionRef.current = new Draw({
      source: source,
      type: drawType,
      style: new Style({
        fill: new Fill({
          color: 'rgba(239, 68, 68, 0.2)',
        }),
        stroke: new Stroke({
          color: '#ef4444',
          width: 4,
        }),
        image: new CircleStyle({
          radius: 8,
          stroke: new Stroke({
            color: '#ffffff',
            width: 3,
          }),
          fill: new Fill({
            color: '#ef4444',
          }),
        }),
      }),
    });

    map.addInteraction(drawInteractionRef.current);

    createMeasureTooltip();
    createHelpTooltip();

    let sketch;

    drawInteractionRef.current.on('drawstart', (evt) => {
      sketch = evt.feature;
      sketchRef.current = sketch;

      let tooltipCoord = evt.coordinate;

      listenerRef.current = sketch.getGeometry().on('change', (evt) => {
        const geom = evt.target;
        let output;
        if (geom instanceof Point) {
          return;
        }
        if (type === 'area') {
          output = formatArea(geom);
          tooltipCoord = geom.getInteriorPoint().getCoordinates();
        } else {
          output = formatLength(geom);
          tooltipCoord = geom.getLastCoordinate();
        }
        measureTooltipElementRef.current.innerHTML = output;
        measureTooltipRef.current.setPosition(tooltipCoord);
      });
    });

    drawInteractionRef.current.on('drawend', () => {
      measureTooltipElementRef.current.className = 'ol-tooltip ol-tooltip-static';
      // Apply static tooltip styles
      Object.assign(measureTooltipElementRef.current.style, {
        backgroundColor: '#ffcc33',
        color: 'black',
        border: '1px solid white',
        fontWeight: 'bold'
      });
      measureTooltipRef.current.setOffset([0, -7]);
      sketchRef.current = null;
      measureTooltipElementRef.current = null;
      createMeasureTooltip();
      unByKey(listenerRef.current);
    });
  };

  const removeMeasureInteraction = () => {
    if (!mapRef.current) return;

    if (drawInteractionRef.current) {
      mapRef.current.removeInteraction(drawInteractionRef.current);
      drawInteractionRef.current = null;
    }

    if (helpTooltipElementRef.current && helpTooltipElementRef.current.parentNode) {
      helpTooltipElementRef.current.parentNode.removeChild(helpTooltipElementRef.current);
      helpTooltipElementRef.current = null;
    }

    if (measureTooltipElementRef.current && measureTooltipElementRef.current.parentNode) {
      measureTooltipElementRef.current.parentNode.removeChild(measureTooltipElementRef.current);
      measureTooltipElementRef.current = null;
    }

    if (helpTooltipRef.current) {
      mapRef.current.removeOverlay(helpTooltipRef.current);
      helpTooltipRef.current = null;
    }

    if (measureTooltipRef.current) {
      mapRef.current.removeOverlay(measureTooltipRef.current);
      measureTooltipRef.current = null;
    }
  };

  const clearMeasurements = () => {
    if (measureLayerRef.current) {
      measureLayerRef.current.getSource().clear();
    }

    // Remove all measurement tooltips safely
    const tooltips = document.querySelectorAll('.ol-tooltip-static');
    tooltips.forEach((tooltip) => {
      if (tooltip.parentNode) {
        tooltip.parentNode.removeChild(tooltip);
      }
    });
  };

  // Expose methods to parent component
  useImperativeHandle(ref, () => ({
    zoomIn: () => {
      if (mapRef.current) {
        const view = mapRef.current.getView();
        view.animate({ zoom: view.getZoom() + 1, duration: 300 });
      }
    },
    zoomOut: () => {
      if (mapRef.current) {
        const view = mapRef.current.getView();
        view.animate({ zoom: view.getZoom() - 1, duration: 300 });
      }
    },
    toggleFullscreen: () => {
      if (mapContainerRef.current) {
        if (!document.fullscreenElement) {
          mapContainerRef.current.requestFullscreen();
        } else {
          document.exitFullscreen();
        }
      }
    },
    measureDistance: () => {
      if (measurementType === 'length') {
        setMeasurementType(null);
      } else {
        setMeasurementType('length');
      }
    },
    measureArea: () => {
      if (measurementType === 'area') {
        setMeasurementType(null);
      } else {
        setMeasurementType('area');
      }
    },
    clearMeasurements: clearMeasurements,
    goToCurrentLocation: () => {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const { latitude, longitude } = position.coords;
            const coordinate = fromLonLat([longitude, latitude]);
            const view = mapRef.current.getView();

            // Zoom to current location
            view.animate({
              center: coordinate,
              zoom: 16,
              duration: 1000,
            });

            // Remove previous location marker if exists
            if (currentLocationMarkerRef.current) {
              mapRef.current.removeLayer(currentLocationMarkerRef.current);
            }

            // Add a marker for current location
            const markerFeature = new Feature({
              geometry: new Point(coordinate),
              name: 'Your Location',
            });

            const markerStyle = new Style({
              image: new CircleStyle({
                radius: 8,
                fill: new Fill({
                  color: '#3b82f6',
                }),
                stroke: new Stroke({
                  color: '#ffffff',
                  width: 3,
                }),
              }),
            });

            markerFeature.setStyle(markerStyle);

            const vectorSource = new VectorSource({
              features: [markerFeature],
            });

            const markerLayer = new VectorLayer({
              source: vectorSource,
            });

            mapRef.current.addLayer(markerLayer);
            currentLocationMarkerRef.current = markerLayer;
          },
          (error) => {
            alert('Unable to get your location. Please enable location services.');
          }
        );
      } else {
        alert('Geolocation is not supported by your browser.');
      }
    }
  }));

  // Initialize map
  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) return;

    const COG_API_BASE_URL = 'https://backend.geonetra.tech';
    const COG_DATASET_URL = 'https://panaji.s3.ap-south-1.amazonaws.com/panaji_cog.tif';

    // COG dataset bounds from info.geojson
    const cogBounds = [73.78311661339146, 15.44321668145216, 73.89041785265627, 15.510058224743702];

    // Create OSM base layer (hidden by default, COG will be the base)
    const osmLayer = new TileLayer({
      source: new OSM(),
      visible: false,
    });

    osmLayerRef.current = osmLayer;

    // Create COG overlay layer using /cog/cog/tiles/{tileMatrixSetId}/{z}/{x}/{y}@{scale}x format
    // Using WebMercatorQuad to match OSM projection
    const cogTileUrl = `https://backend.geonetra.tech/cog/cog/tiles/WebMercatorQuad/{z}/{x}/{y}@2x?url=${encodeURIComponent(COG_DATASET_URL)}&resampling=lanczos`;

    // COG drone imagery as the permanent base layer
    const cogLayer = new TileLayer({
      source: new XYZ({
        url: cogTileUrl,
        crossOrigin: 'anonymous',
        minZoom: 10,
        maxZoom: 20,
      }),
      visible: true, // Always visible
      opacity: 1.0,
    });

    // Add error handling for tile loading
    cogLayer.getSource().on('tileloaderror', function(event) {
    });

    cogLayer.getSource().on('tileloadstart', function(event) {
    });

    cogLayer.getSource().on('tileloadend', function() {
    });

    cogLayerRef.current = cogLayer;

    // Calculate center of COG bounds
    const cogCenterLon = (cogBounds[0] + cogBounds[2]) / 2;
    const cogCenterLat = (cogBounds[1] + cogBounds[3]) / 2;

    const map = new Map({
      target: mapContainerRef.current,
      layers: [osmLayer, cogLayer],
      view: new View({
        center: fromLonLat([cogCenterLon, cogCenterLat]),
        zoom: 14,
      }),
      controls: [], // Remove default controls including zoom buttons
    });

    // Create popup overlay
    const overlay = new Overlay({
      element: popupRef.current,
      autoPan: {
        animation: {
          duration: 250,
        },
      },
    });
    map.addOverlay(overlay);
    overlayRef.current = overlay;

    // Update cursor when hovering over features
    map.on('pointermove', (e) => {
      const features = map.getFeaturesAtPixel(e.pixel);
      map.getTargetElement().style.cursor = features && features.length > 0 ? 'pointer' : '';
    });

    // Handle map click
    map.on('click', (e) => {
      // Don't trigger popup when measuring
      if (measurementTypeRef.current) {
        return;
      }

      const [lon, lat] = toLonLat(e.coordinate);

      // Check for features at the clicked location
      const features = map.getFeaturesAtPixel(e.pixel);

      if (features && features.length > 0) {
        const feature = features[0];
        const properties = feature.getProperties();

        // Remove geometry from properties for cleaner display
        const { geometry, ...attributes } = properties;


        // Show popup with feature attributes
        setFeatureInfo(attributes);
        overlay.setPosition(e.coordinate);

        // Pass both coordinates and feature attributes to parent
        onMapClick && onMapClick({
          lat,
          lon,
          feature: attributes
        });
      } else {
        // Close popup if clicking on empty area
        setFeatureInfo(null);
        overlay.setPosition(undefined);
        onMapClick && onMapClick({ lat, lon });
      }
    });

    mapRef.current = map;

    return () => {
      if (mapRef.current) {
        mapRef.current.setTarget(null);
        mapRef.current = null;
      }
    };
  }, [onMapClick]);

  // Handle measurement type changes
  useEffect(() => {
    if (!mapRef.current) return;

    measurementTypeRef.current = measurementType;
    removeMeasureInteraction();

    if (measurementType) {
      addMeasureInteraction(measurementType);
    }

    return () => {
      removeMeasureInteraction();
    };
  }, [measurementType]);


  // Store layer metadata when it changes
  useEffect(() => {
    if (layerMetadata) {
      layerMetadataRef.current = layerMetadata;
    }
  }, [layerMetadata]);

  // Update layers when props change
  useEffect(() => {

    if (!mapRef.current || !layers) return;

    const map = mapRef.current;
    updateLayers(map, layers);
  }, [layers]);

  // Handle search location
  useEffect(() => {
    if (!mapRef.current || !searchLocation) return;

    const map = mapRef.current;
    const view = map.getView();
    const coordinate = fromLonLat([searchLocation.lon, searchLocation.lat]);

    // Zoom to the location
    view.animate({
      center: coordinate,
      zoom: 16,
      duration: 1000,
    });

    // Remove previous search marker if exists
    if (searchMarkerRef.current) {
      map.removeLayer(searchMarkerRef.current);
    }

    // Add a marker for the search location
    const markerFeature = new Feature({
      geometry: new Point(coordinate),
      name: searchLocation.name,
    });

    const markerStyle = new Style({
      image: new CircleStyle({
        radius: 8,
        fill: new Fill({
          color: '#ef4444',
        }),
        stroke: new Stroke({
          color: '#ffffff',
          width: 3,
        }),
      }),
    });

    markerFeature.setStyle(markerStyle);

    const vectorSource = new VectorSource({
      features: [markerFeature],
    });

    const markerLayer = new VectorLayer({
      source: vectorSource,
    });

    map.addLayer(markerLayer);
    searchMarkerRef.current = markerLayer;
  }, [searchLocation]);

  const fetchAndZoomToLayer = async (map, layerId) => {
    const metadata = layerMetadataRef.current[layerId];
    if (!metadata || !metadata.id) {
      return;
    }

    try {
      const metadataResponse = await fetch(
        `https://backend.geonetra.tech/api/v1/fgbtiles/metadata?fgb_item_id=${metadata.id}&schema=public`
      );
      const layerMetadataInfo = await metadataResponse.json();

      if (layerMetadataInfo.bounds && layerMetadataInfo.bounds.length === 4) {
        // Store bounds for future use
        layerMetadataRef.current[layerId] = {
          ...layerMetadataRef.current[layerId],
          bounds: layerMetadataInfo.bounds
        };

        const [minLng, minLat, maxLng, maxLat] = layerMetadataInfo.bounds;
        const bottomLeft = fromLonLat([minLng, minLat]);
        const topRight = fromLonLat([maxLng, maxLat]);
        const extent = [bottomLeft[0], bottomLeft[1], topRight[0], topRight[1]];

        map.getView().fit(extent, {
          padding: [50, 50, 50, 50],
          duration: 1000,
        });
      }
    } catch (error) {
    }
  };

  const updateLayers = async (map, layers) => {

    // Get active layer IDs
    const activeLayerIds = getActiveLayerIds(layers, layerMetadataRef.current);


    // Hide all individual layers (we don't use them anymore)
    Object.keys(layersRef.current).forEach((layerKey) => {
      if (layersRef.current[layerKey]) {
        layersRef.current[layerKey].setVisible(false);
      }
    });

    // Hide multi-layer if it exists
    if (multiLayerRef.current) {
      multiLayerRef.current.setVisible(false);
    }

    // If no active layers, return early
    if (activeLayerIds.length === 0) {
      return;
    }

    // ALWAYS USE MULTI-LAYER API (even for single layers)
    await addMultiLayer(map, activeLayerIds);

    // Log final state
  };

  const addMultiLayer = async (map, layerIds) => {

    try {
      // Fetch combined metadata for all layers
      const multiMetadata = await fetchMultiLayerMetadata(layerIds);


      // Create multi-layer tile URL template
      const tileUrlTemplate = createMultiLayerUrlTemplate(layerIds);


      // Get source layer names from all layers
      const sourceLayerNames = multiMetadata.layers
        .map(m => m.json?.vector_layers?.[0]?.id)
        .filter(Boolean);


      // Don't specify layerName to load ALL layers from the tile
      // The multi-layer endpoint returns multiple layers in one PBF
      // Use Feature class instead of RenderFeature for easier manipulation
      const mvtFormat = new MVT({
        featureClass: Feature, // Use regular Feature objects, not RenderFeature
      });


      const vectorTileSource = new VectorTileSource({
        format: mvtFormat,
        url: tileUrlTemplate,
        minZoom: 0, // Allow tiles at all zoom levels
        maxZoom: 22,
        // Custom tile loader to handle the JSON response format
        tileLoadFunction: function(tile, url) {

          fetch(url)
            .then(response => {

              if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
              }

              // The endpoint returns JSON, not raw PBF
              return response.json();
            })
            .then(jsonData => {

              // Collect all features from all layers
              const allFeatures = [];
              const format = tile.getFormat();

              // Process each layer's PBF data
              for (const [layerId, layerData] of Object.entries(jsonData.layers || {})) {

                if (layerData.pbf_data) {
                  try {
                    // Decode base64 PBF data
                    const base64Data = layerData.pbf_data;
                    const binaryString = atob(base64Data);
                    const bytes = new Uint8Array(binaryString.length);
                    for (let i = 0; i < binaryString.length; i++) {
                      bytes[i] = binaryString.charCodeAt(i);
                    }


                    // Parse PBF as MVT
                    const features = format.readFeatures(bytes.buffer, {
                      extent: tile.extent,
                      featureProjection: tile.projection,
                    });


                    // Add layer ID to each feature for styling
                    features.forEach(feature => {
                      // Check if it's already an OpenLayers Feature
                      if (typeof feature.set === 'function') {
                        feature.set('fgb_item_id', layerId);
                      } else if (feature.properties) {
                        // If it's a plain object with properties, add the ID there
                        feature.properties = feature.properties || {};
                        feature.properties.fgb_item_id = layerId;
                      } else {
                      }
                    });

                    allFeatures.push(...features);
                  } catch (error) {
                  }
                }
              }


              if (allFeatures.length > 0) {
                tile.setFeatures(allFeatures);
                tile.setState(2); // LOADED
              } else {
                tile.setFeatures([]);
                tile.setState(2); // LOADED but empty
              }
            })
            .catch(error => {
              tile.setState(3); // ERROR
            });
        },
      });

      // Add error handling for tile loading
      vectorTileSource.on('tileloaderror', function(event) {

        // Try to fetch the tile manually to see the actual error
        fetch(event.tile.key)
          .then(response => {
            return response.blob();
          })
          .then(blob => {
          })
      });

      vectorTileSource.on('tileloadstart', function(event) {
      });

      vectorTileSource.on('tileloadend', function(event) {
        const features = event.tile.getFeatures();
        if (features && features.length > 0) {
        }
      });

      // Create a style function that applies symbology based on layer description
      const styleFunction = (feature, resolution) => {
        // Get the source layer name (which might contain the layer ID)
        const sourceLayer = feature.get('layer');

        // Try to get layer metadata from feature properties
        const fgbItemId = feature.get('fgb_item_id');


        // Find the corresponding layer metadata
        let layerMeta = null;

        // First try to match by fgb_item_id
        if (fgbItemId) {
          layerMeta = layerMetadataRef.current[fgbItemId];
        }

        // If not found, try to find by checking all metadata IDs
        if (!layerMeta) {
          for (const [layerKey, metadata] of Object.entries(layerMetadataRef.current)) {
            if (metadata.id === fgbItemId || layerKey === fgbItemId) {
              layerMeta = metadata;
              break;
            }
          }
        }

        // Apply symbology based on layer metadata
        if (layerMeta) {
          return getLayerSymbology(layerMeta);
        }

        // Default style if no metadata found
        return new Style({
          fill: new Fill({
            color: 'rgba(156, 163, 175, 0.4)',
          }),
          stroke: new Stroke({
            color: '#6b7280',
            width: 1,
          }),
          image: new CircleStyle({
            radius: 4,
            fill: new Fill({ color: '#9ca3af' }),
            stroke: new Stroke({ color: '#ffffff', width: 1 }),
          }),
        });
      };

      // Reuse existing multi-layer if it exists, otherwise create new one
      if (multiLayerRef.current) {
        // Update the source and style without removing/re-adding the layer
        multiLayerRef.current.setSource(vectorTileSource);
        multiLayerRef.current.setStyle(styleFunction);
        multiLayerRef.current.setVisible(true);
      } else {
        // Create Vector Tile layer for multi-layer visualization
        const multiVectorLayer = new VectorTileLayer({
          source: vectorTileSource,
          style: styleFunction,
          zIndex: 100, // Ensure it's on top
          visible: true, // Explicitly set visible
        });


        // Add layer to map
        map.addLayer(multiVectorLayer);
        multiLayerRef.current = multiVectorLayer;
      }


      // Force a render
      map.render();

      // No automatic zooming - preserve user's current view position
    } catch (error) {
    }
  };

  const addLayerByType = async (map, layerId, shouldZoom = false) => {
    // Get metadata for this layer
    const metadata = layerMetadataRef.current[layerId];
    if (!metadata || !metadata.id) {
      return;
    }

    try {
      // Fetch layer metadata including bounds from the API
      const metadataResponse = await fetch(
        `https://backend.geonetra.tech/api/v1/fgbtiles/metadata?fgb_item_id=${metadata.id}&schema=public`
      );
      const layerMetadataInfo = await metadataResponse.json();


      // Get the source layer name from metadata
      const sourceLayerName = layerMetadataInfo.json?.vector_layers?.[0]?.id || 'fgbfeatures';

      // Determine geometry type from metadata
      const geometryType = layerMetadataInfo.json?.vector_layers?.[0]?.geometry_type;
      const geometryTypeLower = geometryType?.toLowerCase();


      // Create Vector Tile source
      const vectorTileSource = new VectorTileSource({
        format: new MVT({
          layerName: sourceLayerName,
        }),
        url: `https://backend.geonetra.tech/api/v1/fgbtiles/${metadata.id}/{z}/{x}/{y}.pbf`,
        minZoom: layerMetadataInfo.minzoom || 0,
        maxZoom: layerMetadataInfo.maxzoom || 22,
      });

      // Get symbology based on layer description
      const layerSymbology = getLayerSymbology(metadata);

      // Create style function that applies the symbology
      const styleFunction = (feature) => {
        return layerSymbology;
      };

      // Create Vector Tile layer
      const vectorTileLayer = new VectorTileLayer({
        source: vectorTileSource,
        style: styleFunction,
      });

      // Add layer to map
      map.addLayer(vectorTileLayer);
      layersRef.current[layerId] = vectorTileLayer;

      // Store bounds in metadata for later use
      if (layerMetadataInfo.bounds) {
        layerMetadataRef.current[layerId] = {
          ...layerMetadataRef.current[layerId],
          bounds: layerMetadataInfo.bounds
        };
      }


      // Zoom to layer bounds if shouldZoom is true (for newly added layers)
      if (shouldZoom && layerMetadataInfo.bounds && layerMetadataInfo.bounds.length === 4) {
        const [minLng, minLat, maxLng, maxLat] = layerMetadataInfo.bounds;
        const bottomLeft = fromLonLat([minLng, minLat]);
        const topRight = fromLonLat([maxLng, maxLat]);
        const extent = [bottomLeft[0], bottomLeft[1], topRight[0], topRight[1]];


        map.getView().fit(extent, {
          padding: [50, 50, 50, 50],
          duration: 1000,
        });
      }

    } catch (error) {
    }
  };

  const closePopup = () => {
    setFeatureInfo(null);
    if (overlayRef.current) {
      overlayRef.current.setPosition(undefined);
    }
  };

  return (
    <div className="w-full h-full relative">
      <div
        ref={mapContainerRef}
        className="w-full h-full"
      />

      {/* Feature Info Popup */}
      <div
        ref={popupRef}
        className="absolute bg-white rounded-lg shadow-lg border border-gray-200 min-w-[250px] max-w-[400px] pointer-events-auto"
        style={{ display: featureInfo ? 'block' : 'none' }}
      >
        <div className="flex items-center justify-between bg-blue-600 text-white px-3 py-2 rounded-t-lg">
          <h3 className="text-sm font-semibold">Feature Information</h3>
          <button
            onClick={closePopup}
            className="hover:bg-blue-700 rounded p-1 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-3 max-h-[300px] overflow-y-auto">
          {featureInfo && Object.keys(featureInfo).length > 0 ? (
            <table className="w-full text-sm">
              <tbody>
                {Object.entries(featureInfo)
                  .filter(([key]) => key !== 'id' && key !== 'fgb_item_id' && key !== 'z_value' && key !== 'fgbfeatures')
                  .map(([key, value]) => (
                    <tr key={key} className="border-b border-gray-100 last:border-b-0">
                      <td className="py-2 pr-3 font-medium text-gray-700 align-top">{key}</td>
                      <td className="py-2 text-gray-900 break-words">{String(value)}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          ) : (
            <p className="text-gray-500 text-sm">No attributes available</p>
          )}
        </div>
      </div>



      {/* Measurement controls - Center top */}
      <div className="absolute top-2 md:top-4 left-1/2 transform -translate-x-1/2 flex flex-row gap-2 z-10 pointer-events-auto">
        <button
          onClick={() => setMeasurementType(measurementType === 'length' ? null : 'length')}
          className={`p-2 md:p-3 rounded-lg shadow-lg transition-all ${
            measurementType === 'length'
              ? 'bg-blue-600 text-white'
              : 'bg-white text-gray-700 hover:bg-gray-100'
          }`}
          title="Measure Distance"
        >
          <Ruler className="w-4 h-4 md:w-5 md:h-5" />
        </button>
        <button
          onClick={() => setMeasurementType(measurementType === 'area' ? null : 'area')}
          className={`p-2 md:p-3 rounded-lg shadow-lg transition-all ${
            measurementType === 'area'
              ? 'bg-blue-600 text-white'
              : 'bg-white text-gray-700 hover:bg-gray-100'
          }`}
          title="Measure Area"
        >
          <Square className="w-4 h-4 md:w-5 md:h-5" />
        </button>
        {measurementType && (
          <button
            onClick={() => {
              clearMeasurements();
              setMeasurementType(null);
            }}
            className="p-2 md:p-3 bg-red-600 text-white rounded-lg shadow-lg hover:bg-red-700 transition-all"
            title="Clear Measurements"
          >
            <X className="w-4 h-4 md:w-5 md:h-5" />
          </button>
        )}
      </div>

      {/* Measurement instructions */}
      {measurementType && (
        <div className="absolute top-14 md:top-16 left-1/2 transform -translate-x-1/2 bg-white px-3 py-2 rounded-lg shadow-lg text-xs md:text-sm z-10 max-w-[200px] md:max-w-xs pointer-events-none text-center">
          <p className="font-semibold text-gray-900 mb-1">
            {measurementType === 'length' ? 'Measure Distance' : 'Measure Area'}
          </p>
          <p className="text-gray-600">
            {measurementType === 'length'
              ? 'Click to start drawing a line. Double-click to finish.'
              : 'Click to draw a polygon. Double-click to finish.'}
          </p>
        </div>
      )}
    </div>
  );
});

export default MapComponent;
